// Recaudo de la Tienda — Wompi + Rapyd + transferencia. Factura = Siigo (Alebas).
// Las llaves viven en el hub-api (mismos nombres que FluxAI). Acá solo se pide el checkout.
import { hubGet, hubPost } from './hub';
import { tokenParaBackend } from './adminAuth';
import { getToken } from './auth';

/** El JWT de la suite (el mismo que usa la Tienda para el precio de técnico). */
function tokenDeLaSuite() {
  try {
    return getToken() || '';
  } catch {
    return '';
  }
}

/**
 * Mete el token de la suite en el cuerpo. `hubPost` no manda cabeceras propias —igual que en las
 * rutas de administración—, así que el token viaja en `body.token`, que es de donde lo lee
 * `tokenSesion(req)` en el servidor.
 */
function conSesion(body) {
  const t = tokenDeLaSuite();
  return t ? { ...(body || {}), token: (body && body.token) || t } : (body || {});
}

export function urlDeCheckout(checkout, retorno) {
  if (!checkout) return null;
  if (checkout.tipo === 'redirect' && checkout.url) return checkout.url;
  if (checkout.url) return checkout.url;
  if (checkout.tipo === 'widget' && checkout.wompi) {
    const w = checkout.wompi;
    const params = new URLSearchParams({
      'public-key': w.public_key,
      currency: w.currency,
      'amount-in-cents': String(w.amount_in_cents),
      reference: w.reference,
      'signature:integrity': w.signature,
      ...(retorno ? { 'redirect-url': retorno } : {}),
    });
    return `https://checkout.wompi.co/p/?${params.toString()}`;
  }
  return null;
}

export async function listarMediosPago() {
  return hubGet('pagos/medios');
}

export async function crearCheckoutTienda(body) {
  return hubPost('pagos/checkout', body);
}

export async function confirmarRetornoPago(referencia) {
  return hubPost('pagos/confirmar-retorno', { referencia });
}

/**
 * 🔒 Es del ADMIN, no del comprador. El nombre engaña —quedó de cuando se pensaba una pantalla de
 * cliente que nunca se construyó—: quien la usa es `PedidoFiscal`, en la pestaña de facturación
 * del BackOffice, buscando los pedidos de un correo. Desde el 2026-09-07 el hub-api exige el token
 * de administración: sin él devolvía los pedidos, el total y el documento de cualquiera.
 */
export async function listarMisPedidosRemotos(email) {
  return hubGet('pagos/mios', { email }, auth());
}

/** Ingresos del técnico (80% de la labor, cuando la obra ya está hecha). */
/**
 * 🔒 Es la plata de una persona: el hub-api responde 401 sin sesión de la suite verificada, así que
 * el token va por cabecera. Un panel vacío por falta de sesión NO es «no te debemos nada»: la
 * pantalla tiene que mostrar el motivo que devuelve el servidor.
 */
export async function listarPagosTecnico(email, techId) {
  const q = {};
  if (email) q.email = email;
  if (techId != null && techId !== '') q.tech_id = techId;
  const t = tokenDeLaSuite();
  return hubGet('pagos/tecnico', q, t ? { headers: { Authorization: `Bearer ${t}` } } : {});
}

/** Admin registra el giro (Bre-B / transferencia / misma suite que FluxAI). */
export async function girarPagoTecnico(body) {
  const token = body?.token || tokenParaBackend() || '';
  return hubPost('pagos/tecnico/girar', { ...body, token });
}

/** A dónde le giran al técnico. Lo registra ÉL; sin esto el giro no sale. */
/**
 * 🔒 El destino de un giro es dato sensible: el hub-api devuelve la cuenta y la cédula COMPLETAS
 * solo a la sesión de la suite del dueño de ese correo (o a un admin); a cualquier otro se las
 * enmascara y responde `completo:false`. Por eso va el token: sin él, el propio técnico vería su
 * cuenta tapada. Ver `server/identidad.js`.
 */
export async function verMedioTecnico(email) {
  const t = tokenDeLaSuite();
  return hubGet('pagos/tecnico/medio', { email }, t ? { headers: { Authorization: `Bearer ${t}` } } : {});
}

/** Y para ESCRIBIRLO hace falta sí o sí: sin sesión verificada el hub-api responde 401. */
export async function guardarMedioTecnico(body) {
  return hubPost('pagos/tecnico/medio', conSesion(body));
}

// ── Recaudo: qué medios hay y cuánto cuesta cada uno ANTES de cobrar ─────────────────────────
export async function opcionesDeRecaudo({ monto, moneda = 'COP', pais = 'CO', medio } = {}) {
  const q = { monto, moneda, pais };
  if (medio) q.medio = medio;
  return hubGet('pagos/pasarelas', q);
}

// ── Medio anclado: se autoriza una vez y después se debita sin el titular presente ───────────
// 🔐 El número de la tarjeta NO pasa por Solar Hub ni por el hub-api: se tokeniza en el
// navegador contra Wompi con la llave PÚBLICA (`tokenizarTarjetaWompi`) y acá solo viaja el
// `tok_…` de un solo uso. Mandar el PAN al backend nos metería en alcance PCI-DSS completo.
export async function datosDeAnclaje() {
  return hubGet('pagos/anclaje');
}

/**
 * Tokeniza la tarjeta contra Wompi DESDE EL NAVEGADOR.
 * @param {{numero:string,cvc:string,mes:string,anio:string,titular:string}} tarjeta
 */
/**
 * 🔴 EL DEFAULT SE DERIVA DE LA LLAVE, NO SE ASUME SANDBOX.
 *
 * Antes el default era `https://sandbox.wompi.co/v1`, así que si el llamador olvidaba la base
 * —cosa que pasó al traer la llave del motor— se tokenizaba una tarjeta REAL con la llave de
 * PRODUCCIÓN contra la API de PRUEBAS. Wompi rechaza eso, y el mensaje que ve el titular es
 * «Wompi no aceptó la tarjeta»: señala a la tarjeta y no al bug, así que el cliente se va
 * creyendo que su tarjeta no sirve.
 *
 * La llave y la base tienen que ser del MISMO ambiente. Como la llave ya dice cuál es
 * (`pub_prod_…` vs `pub_test_…`), la base sale de ahí y deja de ser un dato que se puede olvidar.
 */
export function baseWompiDe(publicKey) {
  return String(publicKey || '').startsWith('pub_prod_')
    ? 'https://production.wompi.co/v1'
    : 'https://sandbox.wompi.co/v1';
}

export async function tokenizarTarjetaWompi(tarjeta, publicKey, base = baseWompiDe(publicKey)) {
  const r = await fetch(`${base}/tokens/cards`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${publicKey}` },
    body: JSON.stringify({
      number: String(tarjeta.numero || '').replace(/\s+/g, ''),
      cvc: tarjeta.cvc,
      exp_month: tarjeta.mes,
      exp_year: tarjeta.anio,
      card_holder: tarjeta.titular,
    }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j?.data?.id) {
    return { ok: false, error: j?.error?.reason || 'Wompi no aceptó la tarjeta.' };
  }
  return { ok: true, token: String(j.data.id), marca: j.data.brand, ultimos4: j.data.last_four };
}

export async function anclarMedioPago(body) {
  return hubPost('pagos/anclaje', body);
}

/**
 * Sigue abierta a propósito —el comprador no tiene sesión y tiene que poder ver su propia tarjeta
 * guardada—, pero desde el 2026-09-07 **lo que llega depende de la credencial**: con sesión de la
 * suite viene completo (`completo:true`); sin ella, solo marca, últimos 4, estado y fecha. Por eso
 * se manda el token cuando existe: el técnico y el admin sí lo tienen.
 */
export async function listarMediosAnclados(email, tipo = 'cliente') {
  const t = tokenDeLaSuite();
  return hubGet('pagos/medios-anclados', { email, tipo }, t ? { headers: { Authorization: `Bearer ${t}` } } : {});
}

export async function retirarMedioAnclado(body) {
  return hubPost('pagos/medios-anclados/retirar', conSesion(body));
}

/** Débito contra el medio anclado (admin). El webhook es el que cierra el pedido. */
export async function debitarPedido(body) {
  const token = body?.token || tokenParaBackend() || '';
  return hubPost('pagos/debitar', { ...body, token });
}

// ── Siigo: todas las herramientas (admin) ────────────────────────────────────────────────────
const conToken = (body = {}) => ({ ...body, token: body.token || tokenParaBackend() || '' });

// El guard de administración lee `Authorization` (o `body.token`), NUNCA el query string: un
// token por la URL da 401 silencioso y además queda escrito en los logs de acceso. Es el
// mismo defecto que tuvo `list-quotes`, que «nunca funcionó desde el cliente».
const auth = () => {
  const t = tokenParaBackend();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export async function estadoSiigo() {
  return hubGet('pagos/siigo', {}, auth());
}

export async function catalogosSiigo() {
  return hubGet('pagos/siigo/catalogos', {}, auth());
}

/** Qué le falta al cliente ANTES de emitir. Una FV mal formada quema consecutivo DIAN. */
export async function requisitosDeFactura(pedidoId) {
  return hubGet(`pagos/pedido/${encodeURIComponent(pedidoId)}/requisitos-factura`);
}

export async function emitirFacturaPedido(pedidoId, body = {}) {
  return hubPost(`pagos/pedido/${encodeURIComponent(pedidoId)}/factura`, conToken(body));
}

export async function notaCreditoPedido(pedidoId, body = {}) {
  return hubPost(`pagos/pedido/${encodeURIComponent(pedidoId)}/nota-credito`, conToken(body));
}

export async function notaDebitoPedido(pedidoId, body = {}) {
  return hubPost(`pagos/pedido/${encodeURIComponent(pedidoId)}/nota-debito`, conToken(body));
}

export async function reciboCajaPedido(pedidoId, body = {}) {
  return hubPost(`pagos/pedido/${encodeURIComponent(pedidoId)}/recibo-caja`, conToken(body));
}

export async function consultarDocumentoSiigo(id, tipo = 'factura') {
  return hubGet(`pagos/siigo/documento/${encodeURIComponent(id)}`, { tipo }, auth());
}

export async function listarDocumentosSiigo(q = {}) {
  return hubGet('pagos/siigo/documentos', q, auth());
}

/** Vista de flota para el admin: TODOS los pedidos recientes con su estado fiscal. Habeas Data → auth. */
export async function listarPedidosAdmin(q = {}) {
  return hubGet('pagos/pedidos', q, auth());
}

export async function pdfDocumentoSiigo(id, tipo = 'factura') {
  return hubGet(`pagos/siigo/documento/${encodeURIComponent(id)}/pdf`, { tipo }, auth());
}

export async function reenviarDocumentoSiigo(id, body = {}) {
  return hubPost(`pagos/siigo/documento/${encodeURIComponent(id)}/correo`, conToken(body));
}

export async function estamparDocumentoSiigo(id, body = {}) {
  return hubPost(`pagos/siigo/documento/${encodeURIComponent(id)}/estampar`, conToken(body));
}

export async function erroresDianSiigo(id, tipo = 'factura') {
  return hubGet(`pagos/siigo/documento/${encodeURIComponent(id)}/errores`, { tipo }, auth());
}

/** ⚠️ Una FV ya estampada NO se anula: se corrige con nota crédito. */
export async function anularDocumentoSiigo(id, body = {}) {
  return hubPost(`pagos/siigo/documento/${encodeURIComponent(id)}/anular`, conToken(body));
}

// ── La cadena automática (admin) ─────────────────────────────────────────────────────────────
/** Qué se hizo solo y qué se rindió. Un trabajo rendido es plata cobrada sin documento. */
export async function verColaPagos(incluirHechos = false) {
  return hubGet('pagos/cola', incluirHechos ? { todos: '1' } : {}, auth());
}

export async function procesarColaPagos(body = {}) {
  return hubPost('pagos/cola/procesar', conToken(body));
}

/** Fuerza una corrida del débito mensual (normalmente lo dispara el cron). */
export async function cobrarRecurrentes(body = {}) {
  return hubPost('pagos/recurrentes/cobrar', conToken(body));
}

/**
 * ARQUEO DE INGRESOS: qué entró en el período y si todo lo cobrado terminó facturado. Es de
 * administración —son las cuentas de la empresa— y el hub-api deja rastro de quién lo miró.
 */
export async function arqueoDeIngresos({ desde, hasta } = {}) {
  return hubGet('pagos/ingresos', { desde, hasta }, auth());
}

// ── Lista oficial de los servicios de la suite ───────────────────────────────────────────────
/** Los precios con los que se cobra un servicio. Vacío = no fijado: no se muestra ni se cobra. */
export async function listaPreciosServicios() {
  return hubGet('pagos/lista-precios');
}

/** Fija los precios. `cambios` es { sku: precio_cop }; null en un SKU lo deja sin precio. */
export async function fijarPreciosServicios(cambios) {
  return hubPost('pagos/lista-precios', conToken({ items: cambios }));
}

/**
 * El % de reinversión de la suite, para ROTULAR. Sale del hub-api, que lo pregunta a la gestión:
 * ninguna pantalla puede volver a escribir «20%» a mano, porque el dueño lo cambia en un solo
 * lado y acá se leería el número viejo.
 */
export async function comisionSuite(concepto = 'instalacion') {
  return hubGet('pagos/comision', { concepto });
}

// ── Visita técnica de validación — el único cobro del cotizador ──────────────────────────────
/** Valor, desglose y reglas. Sale del hub-api: el cotizador NO recalcula la cifra. */
export async function ofertaVisita() {
  return hubGet('pagos/visita');
}

export async function checkoutVisita(body) {
  return hubPost('pagos/visita/checkout', body);
}

/** QR Bancolombia dentro de Solar Hub: los términos de Wompi que el comprador acepta antes. */
export async function terminosPagoQr() {
  return hubGet('pagos/qr/terminos');
}

/** Imagen y estado del QR de un pedido (lo crea el checkout con `qr: true`). */
export async function estadoPagoQr(ref) {
  return hubGet('pagos/qr', { ref });
}

/** Lo que se le descuenta al cliente de la instalación por la visita ya pagada. */
export async function creditoVisita(quoteId) {
  return hubGet('pagos/visita/credito', { quote_id: quoteId });
}

/**
 * 🔒 La agenda de una persona y, del lado del técnico, lo que tiene por cobrar: va con la sesión de
 * la suite, igual que `listarPagosTecnico`. Sin ella el hub-api responde 401 con su motivo, y la
 * pantalla lo muestra — un panel vacío por falta de sesión NO es «no tenés visitas».
 */
export async function misVisitas(email) {
  const t = tokenDeLaSuite();
  return hubGet('pagos/visita/mias', { email }, t ? { headers: { Authorization: `Bearer ${t}` } } : {});
}

/** Visitas pagadas a las que nadie puede cobrarles todavía: falta asignar el técnico. */
export async function visitasSinTecnico() {
  return hubGet('pagos/visita/sin-tecnico', {}, auth());
}

export async function asignarTecnicoVisita(visitaId, body = {}) {
  return hubPost(`pagos/visita/${encodeURIComponent(visitaId)}/tecnico`, conToken(body));
}

/**
 * ANULAR una visita pagada y emitir su nota crédito.
 *
 * 🔴 La ruta existía desde el arranque del cobro de visitas y NADIE la llamaba: una visita
 * cobrada por error se quedaba en la cola para siempre, porque la única acción de esa lista era
 * asignarle un técnico. Cobrar sin poder devolver no es medio cobro: es un reclamo.
 *
 * 🪤 Falla CERRADO: si ya se acreditó o liquidó, el servidor responde 409 `no_anulable` — esa
 * plata ya se movió y se corrige en su destino, no borrando la fila de acá.
 */
export async function anularVisita(visitaId, body = {}) {
  return hubPost(`pagos/visita/${encodeURIComponent(visitaId)}/anular`, conToken(body));
}

/**
 * REINTENTAR la radicación del giro del técnico en la gestión suprema.
 *
 * 🔑 El puente falla CERRADO a propósito: si `alebas-gestion` no estaba configurada o no
 * respondió, el 80 % queda liquidado igual y solo `gestion_estado` lo dice. Sin esta función el
 * reintento no tenía quien lo llamara, o sea que ese pago quedaba fuera del ciclo de la empresa
 * **para siempre y sin que nadie se enterara** — que es justo lo que el comentario de la ruta
 * decía que no podía pasar.
 *
 * 🪤 La referencia es determinista (`SH-<order_id>`): reintentar no duplica, la gestión responde
 * `ya_radicada`.
 */
export async function radicarPagoTecnico(orderId, body = {}) {
  return hubPost(`pagos/tecnico/${encodeURIComponent(orderId)}/radicar`, conToken(body));
}
